import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addApi, addUser } from 'shared/src/api'
import {
  MediaQueue as CmsMediaQueue,
  MediaQueueItem,
  isMediaQueueError,
  isMediaQueueItems,
} from 'shared/src/api/types'
import {
  addNavigate,
  addOptionalIntPathParam,
} from 'shared/src/utils/addNavigate'
import {
  addDataLoadingFromPromise,
  hasData,
  isLoading,
} from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../../utils/addCmsLoadingAndErrorIndicators'
import { addCameFromOnboarding } from '../../utils/cameFromOnboardingContext'
import { makeCreatePostRoute } from '../../utils/routes'
import { SocialService, showSocialLinkingErrors } from '../../utils/social'
import Icon from '../Icon'
import { NextActionButton } from '../NextActionButton'
import { OnboardingPage } from '../OnboardingPage'
import { Page } from '../Page'
import {
  BUTTONS_STYLE,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
} from '../styles'
import { addToastContext } from '../toastContext'
import { QueuedImages, SocialSection } from './SocialSection'
import { deleteFromQueue } from './util'

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    paddingTop: 28,
  },
  containerOnboarding: {
    paddingTop: 24,
  },
  socialHeader: {
    fontFamily: SERIF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '21px',
    letterSpacing: '0.02em',
    color: '#232323',
    marginTop: 40,
    marginBottom: 16,
  },
  refreshPendingText: {
    color: 'rgba(54, 52, 50, 0.8)',
    fontSize: 14,
    fontFamily: SANS_SERIF_FONT_FAMILY,
    lineHeight: '160%',
  },
  onboardingHeader: {
    marginBottom: 24,
  },
  skipLinkContainer: {
    height: 48,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: -28,
    marginLeft: 1,
    alignSelf: 'flex-start',
  },
  skipLinkLabel: {
    ...BUTTONS_STYLE,
    color: '#90734F',
    marginRight: 8,
  },
  skipLinkIcon: {
    color: '#90734F',
  },
})

const SkipLink: FC = flowMax(
  addDisplayName('SkipLink'),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({ classes, t }) => (
    <Link to="/account" className={classes.skipLinkContainer}>
      <span className={classes.skipLinkLabel}>
        {t('admin.mediaQueue.skip')}
      </span>
      <Icon name="chevron-right" className={classes.skipLinkIcon} />
    </Link>
  )
)

export const MediaQueue: FC = flowMax(
  addDisplayName('MediaQueue'),
  addUser,
  addApi,
  addNavigate,
  showSocialLinkingErrors,
  addOptionalIntPathParam('userId'),
  addHandlers({
    createMedia:
      ({ navigate, userId }) =>
      (media?: MediaQueueItem) => {
        navigate(makeCreatePostRoute(userId), {
          state: {
            mediaQueueItem: media,
          },
        })
      },
  }),
  addCameFromOnboarding,
  addEffectOnMount(({ cameFromOnboarding, setCameFromOnboarding }) => () => {
    if (!cameFromOnboarding) return

    setCameFromOnboarding(false)
  }),
  addState(
    'cameFromOnboarding',
    '_',
    ({ cameFromOnboarding }) => cameFromOnboarding
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  addWrapper((render, { cameFromOnboarding, classes, t }) =>
    cameFromOnboarding ? (
      <OnboardingPage
        title={t('admin.mediaQueue.titleFirst')}
        headerClassName={classes.onboardingHeader}
      >
        <SkipLink />
        {render()}
      </OnboardingPage>
    ) : (
      <Page leftButton="" title={t('admin.mediaQueue.title')}>
        {render()}
      </Page>
    )
  ),
  addWrapper((render, { cameFromOnboarding, createMedia, classes, t }) => (
    <div
      className={cx(
        classes.container,
        cameFromOnboarding && classes.containerOnboarding
      )}
    >
      <NextActionButton
        icon="create-manually"
        text={t('admin.mediaQueue.createManually')}
        onClick={() => createMedia()}
      />
      {render()}
    </div>
  )),
  addDataLoadingFromPromise(
    'mediaQueue',
    ({ api, userId }) => api.mediaQueue.index({ userId }),
    ['user']
  ),
  addCmsLoadingAndErrorIndicators('mediaQueue'),
  addDataLoadingFromPromise(
    'mediaQueueFresh',
    ({ api, userId }) => api.mediaQueue.index({ userId, forceRefresh: true }),
    ['user']
  ),
  addToastContext,
  addEffect(
    ({ mediaQueueFresh, showErrorToast, t }) =>
      () => {
        if (!hasData(mediaQueueFresh)) {
          return
        }

        const details = t('admin.mediaQueue.refreshErrorDetails')
        if (
          isMediaQueueError(mediaQueueFresh.data.instagram) &&
          isMediaQueueError(mediaQueueFresh.data.tikTok)
        ) {
          showErrorToast({
            title: t('admin.mediaQueue.refreshBothError'),
            details,
          })
        } else if (isMediaQueueError(mediaQueueFresh.data.instagram)) {
          showErrorToast({
            title: t('admin.mediaQueue.refreshInstagramError'),
            details,
          })
        } else if (isMediaQueueError(mediaQueueFresh.data.tikTok)) {
          showErrorToast({
            title: t('admin.mediaQueue.refreshTikTokError'),
            details,
          })
        }
      },
    ['mediaQueueFresh']
  ),
  addProps(({ mediaQueue, mediaQueueFresh }) => ({
    mediaQueue: hasData(mediaQueueFresh)
      ? typedAs<CmsMediaQueue>({
          images: mediaQueueFresh.data.images,
          instagram: isMediaQueueItems(mediaQueueFresh.data.instagram)
            ? mediaQueueFresh.data.instagram
            : mediaQueue.instagram,
          tikTok: isMediaQueueItems(mediaQueueFresh.data.tikTok)
            ? mediaQueueFresh.data.tikTok
            : mediaQueue.tikTok,
        })
      : mediaQueue,
  })),
  addHandlers({
    onDelete:
      ({ mediaQueue, setMediaQueue, setMediaQueueFresh, api }) =>
      (item: MediaQueueItem) => {
        setMediaQueue(deleteFromQueue(item.id, item.sourceType, mediaQueue))
        setMediaQueueFresh(
          deleteFromQueue(item.id, item.sourceType, mediaQueue)
        )

        if (item.sourceType === 'queuedImage') {
          api.queuedImages.delete(item.id)
        } else {
          api.socialMediaPosts.delete(item.id)
        }
      },
  }),
  addProps(({ user }) => ({
    services: typedAs<SocialService[]>(
      user.hasTikTokConnection && !user.hasInstagramConnection
        ? ['tikTok', 'instagram']
        : ['instagram', 'tikTok']
    ),
  })),
  ({
    mediaQueue,
    mediaQueueFresh,
    services,
    createMedia,
    onDelete,
    classes,
    t,
  }) => (
    <>
      {mediaQueue.images.length > 0 && (
        <>
          <h2 className={classes.socialHeader}>
            {t('admin.mediaQueue.imagesSectionTitle')}
          </h2>
          <QueuedImages
            images={mediaQueue.images}
            onSelect={createMedia}
            onDelete={onDelete}
          />
        </>
      )}
      <h2 className={classes.socialHeader}>{t('admin.mediaQueue.social')}</h2>
      {isLoading(mediaQueueFresh) && (
        <h2 className={classes.refreshPendingText}>
          {t('admin.mediaQueue.refreshPending')}
        </h2>
      )}
      {services.map((service, index) => (
        <SocialSection
          service={service}
          mediaQueue={mediaQueue}
          onSelect={createMedia}
          onDelete={onDelete}
          isLast={index === services.length - 1}
          key={service}
        />
      ))}
    </>
  )
)
